import React from 'react';
import NavBarBrand from '@components/NavBarBrand';
import Container from '@components/Container';
import EmptyGroups from '@components/EmptyGroups';
import GroupItem from '@components/GroupItem';
import ModifyGroupDrawer, {type Group} from '@components/ModifyGroupDrawer';
import Toast, {INTENT} from '@components/Toast';
import {
	useMeGroupsQuery,
	useGroupCreateMutation,
	MeGroupsDocument,
} from '@graphql/generated';
import {CustomEventActions} from '@lib/logcenter/eventLog';
import {useLogEvent} from '@providers/LogEventProvider';
import {useUser} from '@providers/UserProvider';
import {useToast, Center, LoadingSpinner, Button, Heading} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import {GROUP_TO_FETCH} from '@constants';

type GroupsPageProps = {
	locale: string;
};

const GroupsPage: React.FC<GroupsPageProps> = ({locale}) => {
	const {eventLogs} = useLogEvent();
	const {user} = useUser();
	const {t} = useTranslation();

	const toast = useToast({
		duration: 3000,
	});

	const [newGroup, setNewGroup] = React.useState<Group | null>(null);

	const {data, loading: groupsLoading} = useMeGroupsQuery({
		variables: {
			first: GROUP_TO_FETCH,
		},
		fetchPolicy: 'cache-and-network',
	});

	const groups = data?.me?.musicTogetherGroups.edges;

	const [createGroup, {loading}] = useGroupCreateMutation({
		onCompleted: ({musicTogetherCreateGroup}) => {
			if (
				musicTogetherCreateGroup.__typename === 'MusicTogetherCreateGroupError'
			) {
				toast.closeAll();

				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			}

			if (
				musicTogetherCreateGroup.__typename === 'MusicTogetherCreateGroupOutput'
			) {
				if (musicTogetherCreateGroup.group.id) {
					setNewGroup({
						groupName: musicTogetherCreateGroup.group.name,
						groupId: musicTogetherCreateGroup.group.id,
					});

					eventLogs?.logCustomEvent({
						groupId: musicTogetherCreateGroup.group.id,
						action: CustomEventActions.createGroup,
					});
				}
			}
		},
		onError: () => {
			toast.closeAll();
			return toast({
				render: () => (
					<Toast intent={INTENT.ERROR}>
						{t('errormessage_text_anerroroccurredpleasetryagain_web')}
					</Toast>
				),
			});
		},
		update: (cache, {data}) => {
			// Manually add group created to me.musicTogetherGroups
			cache.updateQuery(
				{query: MeGroupsDocument, variables: {first: GROUP_TO_FETCH}},
				(dataMe) => {
					if (
						dataMe &&
						data?.musicTogetherCreateGroup.__typename ===
							'MusicTogetherCreateGroupOutput'
					) {
						return {
							me: {
								__typename: 'PrivateUser',
								musicTogetherGroups: {
									edges: [
										{
											__typename:
												'PrivateUserMusicTogetherGroupsConnectionEdge',
											node: data?.musicTogetherCreateGroup?.group,
										},
										...dataMe.me.musicTogetherGroups.edges,
									],
								},
							},
						};
					}

					return dataMe;
				},
			);
		},
	});

	React.useEffect(() => {
		if (user && eventLogs) {
			setTimeout(() => {
				eventLogs.logScreenViewGroups({
					userId: user.id as string,
				});
			}, 100);
		}
	}, [user, eventLogs]);

	if (groupsLoading) {
		return (
			<Center h="100vh">
				<LoadingSpinner />
			</Center>
		);
	}

	if (groups?.length === 0) {
		return <EmptyGroups createGroup={createGroup} locale={locale} />;
	}

	return (
		<>
			<NavBarBrand locale={locale} />
			<Container>
				<Heading
					my="spacing.2xl"
					variant="heading.xl"
					color="text.neutral.primary.default"
				>
					{t('musictogether_title_groupslist_web')}
				</Heading>
				<Button
					size="medium"
					isLoading={loading}
					onClick={() => createGroup()}
					mb="spacing.2xl"
				>
					{t('musictogether_action_createagroup_web')}
				</Button>
				{groups?.map((group) => {
					if (group.node)
						return (
							<GroupItem hasActions key={group.node.id} group={group.node} />
						);
				})}

				{newGroup && (
					<ModifyGroupDrawer
						onCloseModifyGroupDrawer={() => {
							setNewGroup(null);
						}}
						group={newGroup}
					/>
				)}
			</Container>
		</>
	);
};

export default GroupsPage;
